import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';

import { DEAL_STAGE, DealStage } from '@tassomutuo-backoffice/types';
import { FullPageLoader } from '@tassomutuo-backoffice/ui';
import {
  getLeadDetails,
  getLeadsUnderManagement,
  updateLead,
} from '../../services/api/lead/lead';
import LeadDetailsSummaryInfoBox from './components/LeadDetailsSummaryInfoBox';
import LeadDetailsPersonalInfoBox from './components/LeadDetailsPersonalInfoBox';
import LeadDetailsLoanInfoBox from './components/LeadDetailsLoanInfoBox';
import LeadDetailsAppointmentInfoBox from './components/LeadDetailsAppointmentInfoBox';
import ModalLeadChangeStatusForm from './components/ModalLeadChangeStatusForm';
import ModalLeadLoanValuesForm from './components/ModalLeadLoanValuesForm';
import ModalLeadAppointmentForm from './components/ModalLeadAppointmentForm';
import useDateFormat from '../../hooks/useDateFormat';
import LeadsUnderManagementActionSummary from './components/LeadsUnderManagementActionSummary';
import { ActionRequiredType } from './hooks/useLeadActionRequired';
import ModalLeadNotesList from './components/ModalLeadNotesList';
import { logMixPanelEvent } from '../../services/tracking/mixPanel';
import ModalLeadDropForm from './components/ModalLeadDropForm';
import { AddTaskModal } from '../../components';

const LeadDetailsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const { formatDate } = useDateFormat();

  const update = searchParams.get('update') as DealStage;

  const {
    isPending,
    data: leadDetails,
    error,
  } = useQuery({
    queryKey: ['leadDetails', id],
    queryFn: () => getLeadDetails(id!),
    enabled: !!id,
  });

  const { data: leadsUnderManagementData } = useQuery({
    queryKey: ['leadsUnderManagement'],
    queryFn: getLeadsUnderManagement,
  });

  const updateLeadMutation = useMutation({
    mutationFn: updateLead,
    onSuccess: (data, variables, context) => {
      logMixPanelEvent('lead update', { lead_id: id });
      queryClient.invalidateQueries({
        queryKey: ['leadDetails', id],
      });
      queryClient.invalidateQueries({
        queryKey: ['leadsUnderManagement'],
      });

      queryClient.invalidateQueries({
        queryKey: ['leadNotes', id],
      });
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const [showModalChangeStatus, setShowModalChangeStatus] = useState(
    (update && update?.length > 0) || false
  );
  const [showModalDropForm, setShowModalDropForm] = useState(false);
  const [showModalEditLoanValues, setShowModalEditLoanValues] = useState(false);
  const [showModalEditAppointment, setShowModalEditAppointment] =
    useState(false);
  const [showModalListNotes, setShowModalListNotes] = useState(false);
  const [showModalAddTask, setShowModalAddTask] = useState(false);

  const updateLeadHandler = async ({
    dealStage,
    loanAmount,
    propertyValue,
    note,
    droppedMotivation,
    attoDate,
    commission,
    appointmentDate,
    newAppointmentDate,
    appointmentTime,
    research,
    appointmentLocation,
    meeting_done,
    immobile_trovato,
    trattativa_voucher,
  }: {
    dealStage: DealStage;
    loanAmount: string;
    propertyValue: string;
    note: string;
    droppedMotivation: string;
    attoDate: Date | null;
    commission: string;
    appointmentDate: Date | null;
    newAppointmentDate: Date | null;
    appointmentTime: string | null;
    appointmentLocation: string;
    research?: string;
    meeting_done: boolean;
    immobile_trovato?: string;
    trattativa_voucher?: string;
  }) => {
    await updateLeadMutation.mutateAsync({
      actionType: 'updateStatus',
      leadId: id!,
      dealName: leadDetails?.properties.dealname || 'Sconosciuta',
      dealStage,
      currentDealStage: leadDetails?.properties.dealstage || undefined,
      loanAmount,
      propertyValue,
      note,
      research,
      droppedMotivation,
      attoDateTimestamp: attoDate
        ? Date.UTC(
            +formatDate(attoDate, 'yyyy'),
            attoDate.getMonth(),
            +formatDate(attoDate, 'dd')
          )
        : undefined,
      commission,
      appointmentTimestamp: appointmentDate
        ? appointmentDate.getTime()
        : undefined,
      newAppointmentTimestamp: newAppointmentDate
        ? newAppointmentDate.getTime()
        : undefined,
      appointmentTime: appointmentTime || undefined,
      appointmentLocation,
      meeting_done: meeting_done + '' || leadDetails?.properties?.meeting_done,
      salesManagerEmail: leadDetails?.properties?.sales_manager_user?.email,
      immobile_trovato,
      trattativa_voucher,
    });

    if (dealStage === DEAL_STAGE.DROPPED) {
      queryClient.invalidateQueries({
        queryKey: ['leadsDropped'],
      });
    }
    if (dealStage === DEAL_STAGE.ATTO_NOTARILE_CONCLUSO) {
      queryClient.invalidateQueries({
        queryKey: ['leadsClosed'],
      });
    }

    const successMessage =
      dealStage === DEAL_STAGE.DROPPED
        ? 'La tua richiesta di cambio stato a DROPPED è stata inviata'
        : 'Lead modificata con successo';
    toast.success(successMessage);
  };

  const addNoteHandler = async ({
    dealStage,
    note,
  }: {
    dealStage: DealStage | undefined;
    note: string;
  }) => {
    await updateLeadMutation.mutateAsync({
      actionType: 'updateStatus',
      leadId: id!,
      dealName: leadDetails?.properties.dealname || 'Sconosciuta',
      dealStage,
      currentDealStage: leadDetails?.properties.dealstage || undefined,
      note,
    });

    if (dealStage === DEAL_STAGE.DROPPED) {
      queryClient.invalidateQueries({
        queryKey: ['leadsDropped'],
      });
    }
    if (dealStage === DEAL_STAGE.ATTO_NOTARILE_CONCLUSO) {
      queryClient.invalidateQueries({
        queryKey: ['leadsClosed'],
      });
    }

    const successMessage =
      dealStage === DEAL_STAGE.DROPPED
        ? 'La tua richiesta di cambio stato a DROPPED è stata inviata'
        : 'Lead modificata con successo';
    toast.success(successMessage);
  };

  const updateLoanValuesHandler = async ({
    loanAmount,
    propertyValue,
  }: {
    loanAmount: string;
    propertyValue: string;
  }) => {
    await updateLeadMutation.mutateAsync({
      actionType: 'updateLoanValues',
      leadId: id!,
      dealName: leadDetails?.properties.dealname || 'Sconosciuta',
      loanAmount,
      propertyValue,
      salesManagerEmail: leadDetails?.properties?.sales_manager_user?.email,
    });

    toast.success('Valori mutuo modificati con successo');
  };
  const updateAppointmentHandler = async ({
    appointmentDate,
    appointmentTime,
    appointmentLocation,
  }: {
    appointmentDate: Date;
    appointmentTime: string;
    appointmentLocation: string;
  }) => {
    await updateLeadMutation.mutateAsync({
      actionType: 'updateAppointment',
      leadId: id!,
      dealName: leadDetails?.properties.dealname || 'Sconosciuta',
      appointmentTimestamp: appointmentDate.getTime(),
      appointmentTime,
      appointmentLocation,
      salesManagerEmail: leadDetails?.properties?.sales_manager_user?.email,
    });

    toast.success('Appuntamento modificato con successo');
  };

  const onLeadActionCounterClick = (type: ActionRequiredType) => {
    navigate('/lead');
  };

  const handleBackClick = () => {
    const fromParam = searchParams.get('from');

    if (fromParam === 'tasks') {
      navigate('/tasks');
      return;
    }

    if (fromParam === 'tasks-list') {
      navigate('/tasks?tab=list');
      return;
    }

    const isInternalNavigation =
      document.referrer &&
      new URL(document.referrer).origin === window.location.origin;

    if (isInternalNavigation && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/lead');
    }
  };

  return (
    <>
      {/*
      <FireworkAnimation />
*/}
      <FullPageLoader show={isPending} />
      <Box pt={4} mx={2}>
        <Box px={4} pb={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            className="no-print"
            onClick={handleBackClick}
          >
            <ArrowBackIcon />
            <Typography fontWeight="bold" color="primary">
              Indietro
            </Typography>
          </Box>
        </Box>

        {leadsUnderManagementData?.results && (
          <LeadsUnderManagementActionSummary
            leadsUnderManagement={leadsUnderManagementData?.results}
            onLeadActionCounterClick={onLeadActionCounterClick}
          />
        )}

        {!isPending && error && (
          <Alert severity="error">
            {error.message ? `Errore: ${error.message}` : 'An Error occurred'}
          </Alert>
        )}

        {leadDetails && (
          <>
            <LeadDetailsSummaryInfoBox
              lead={leadDetails}
              onUpdateStatusPress={() => setShowModalChangeStatus(true)}
              onShowNotesPress={() => setShowModalListNotes(true)}
              onAddTaskPress={() => setShowModalAddTask(true)}
              onDropPress={() => setShowModalDropForm(true)}
              onSearchingPress={() =>
                addNoteHandler({
                  dealStage: leadDetails.properties.dealstage,
                  note: "Il cliente è ancora in cerca dell'immobile",
                })
              }
              onDevelopPress={() =>
                addNoteHandler({
                  dealStage: DEAL_STAGE.IN_SVILUPPO,
                  note: 'Cliente incontrato e fattibile',
                })
              }
            />
            <Box display="flex" flexWrap="wrap-reverse" alignItems="flex-end">
              <LeadDetailsPersonalInfoBox lead={leadDetails} />
              <LeadDetailsLoanInfoBox
                lead={leadDetails}
                onEditPress={() => setShowModalEditLoanValues(true)}
              />
              <LeadDetailsAppointmentInfoBox
                lead={leadDetails}
                onEditPress={() => setShowModalEditAppointment(true)}
              />
            </Box>
          </>
        )}
      </Box>
      <ModalLeadDropForm
        lead={leadDetails}
        open={showModalDropForm}
        handleClose={() => setShowModalDropForm(false)}
        updateLeadStatus={updateLeadHandler}
      />
      {showModalChangeStatus && (
        <ModalLeadChangeStatusForm
          updating={update}
          lead={leadDetails}
          open={showModalChangeStatus}
          handleClose={() => setShowModalChangeStatus(false)}
          updateLeadStatus={updateLeadHandler}
        />
      )}
      <ModalLeadLoanValuesForm
        lead={leadDetails}
        open={showModalEditLoanValues}
        handleClose={() => setShowModalEditLoanValues(false)}
        updateLoanValues={updateLoanValuesHandler}
      />
      <ModalLeadAppointmentForm
        lead={leadDetails}
        open={showModalEditAppointment}
        handleClose={() => setShowModalEditAppointment(false)}
        updateAppointment={updateAppointmentHandler}
      />
      {showModalListNotes && (
        <ModalLeadNotesList
          open={showModalListNotes}
          handleClose={() => setShowModalListNotes(false)}
          leadId={leadDetails?.id}
        />
      )}

      {showModalAddTask && leadDetails && (
        <AddTaskModal
          open
          onClose={() => setShowModalAddTask(false)}
          lead={leadDetails}
        />
      )}
    </>
  );
};

export default LeadDetailsPage;
